import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { Truck, Camera, MapPin, Wifi, WifiOff, Upload, Check, X, Cloud, CloudOff, Loader2, Layers } from 'lucide-react';
import { useNavigate, useLocation as useRouterLocation } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { useLocation } from '../../contexts/LocationContext';

// Get passed jobId from navigation (e.g., from Job Activity page)
const usePassedJobId = () => {
  const routerLocation = useRouterLocation();
  return routerLocation.state?.jobId as number | undefined;
};
import { JobSelector } from '../common/JobSelector';
import { Job } from '../../types/backend.types';
import { uploadQueueService } from '../../services/upload-queue.service';
import { openNativeCamera, fileToDataUrl } from '../../utils/camera.utils';
import { Camera as CameraComponent } from '../Camera';
import { isIOS, isNativeApp, isNativeIOS, isNativeAndroid } from '../../utils/platform.utils';
import { documentScannerService } from '../../services/documentScanner.service';
import { geofenceService } from '../../services/geofence.service';
import { Camera as CapCamera } from '@capacitor/camera';
// Note: Job mismatch modal is handled globally by GlobalMismatchChecker in App.tsx

interface Employee {
  employee_id: number;
  first_name: string;
  last_name: string;
}

interface JobWithDistance extends Job {
  distance: string;
  distanceValue: number;
  distanceInMiles: number;
  isWithinGeofence: boolean;
}

interface TimeEntry {
  job_id: number;
  job_name: string;
  bradanna_job_number: string;
  clock_in_time: string;
}

interface PendingTicket {
  id: string;
  jobId: number;
  jobName: string;
  truckNumber: string;
  ticketPhoto: File;
  truckPhoto?: File;
  timestamp: number;
}

// Configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';
const NEARBY_RADIUS_MILES = 0.5; // Show jobs within half mile
const SHOP_JOB_IDS = [1, 2]; // Shop job IDs to exclude

// Language support
const translations = {
  en: {
    truckTickets: 'Truck Tickets',
    truckNumber: 'Truck Number',
    next: 'Next',
    clear: 'Clear',
    back: 'Back',
    submit: 'Submit',
    skip: 'Skip',
    retry: 'Retry',
    atLocation: 'At Location',
    milesAway: 'miles away',
    ticketPhoto: 'Ticket Photo',
    truckPhoto: 'Truck Photo (Optional)',
    takePhoto: 'Take Photo',
    success: 'Success!',
    offline: 'Offline Mode',
    pendingTickets: 'pending tickets',
    sync: 'Sync',
    ticketRequired: 'Ticket photo is required',
    enterTruckNumber: 'Enter truck number',
    noJobsNearby: 'No jobs within 0.5 miles. Please clock into the correct job or move closer to a job site.',
    nearestJob: 'Nearest job is',
    away: 'away',
    locationError: 'Unable to get location. Please enable GPS and refresh.',
    cameraError: 'Camera access denied. Please enable camera permissions.',
    uploading: 'Saving...',
    ticketSubmitted: 'Ticket saved! Uploading in background...',
    savedOffline: 'Saved offline. Will sync when online.',
    dailyCount: 'loads today',
    duplicatesIgnored: 'duplicate scan(s) ignored',
    loading: 'Loading...',
    pleaseWait: 'Please wait...',
    tapToTakePhoto: 'Tap to take ticket photo',
    bulkScan: 'Bulk Scan'
  },
  es: {
    truckTickets: 'Boletos de Camión',
    truckNumber: 'Número de Camión',
    next: 'Siguiente',
    clear: 'Limpiar',
    back: 'Atrás',
    submit: 'Enviar',
    skip: 'Omitir',
    retry: 'Reintentar',
    atLocation: 'En Ubicación',
    milesAway: 'millas de distancia',
    ticketPhoto: 'Foto del Boleto',
    truckPhoto: 'Foto del Camión (Opcional)',
    takePhoto: 'Tomar Foto',
    success: '¡Éxito!',
    offline: 'Modo Sin Conexión',
    pendingTickets: 'boletos pendientes',
    sync: 'Sincronizar',
    ticketRequired: 'La foto del boleto es requerida',
    enterTruckNumber: 'Ingrese número de camión',
    noJobsNearby: 'No hay trabajos a 0.5 millas. Favor de marcar en el trabajo correcto o acercarse a un sitio de trabajo.',
    nearestJob: 'El trabajo más cercano está',
    away: 'de distancia',
    locationError: 'No se puede obtener ubicación. Favor de habilitar GPS y actualizar.',
    cameraError: 'Acceso a cámara denegado. Favor de habilitar permisos de cámara.',
    uploading: 'Guardando...',
    ticketSubmitted: '¡Boleto guardado! Subiendo en segundo plano...',
    savedOffline: 'Guardado sin conexión. Se sincronizará cuando esté en línea.',
    dailyCount: 'cargas hoy',
    duplicatesIgnored: 'escaneo(s) duplicado(s) ignorado(s)',
    loading: 'Cargando...',
    pleaseWait: 'Espere por favor...',
    tapToTakePhoto: 'Toque para tomar foto del boleto',
    bulkScan: 'Escaneo Masivo'
  }
};

export const TruckTickets: React.FC = () => {
  // Hooks
  const auth = useContext(AuthContext);
  const navigate = useNavigate();
  const { location, ensureFreshLocation, forceRefreshLocation } = useLocation();

  // Get passed jobId from navigation (e.g., from Job Activity page). Declared up
  // top so the location-staleness guards below can skip the redirect when an
  // admin opened this for a specific job (they don't rely on GPS).
  const passedJobId = usePassedJobId();

  // Navigate with location refresh (matches dashboard pattern)
  const navigateToTimeClock = async () => {
    await ensureFreshLocation();
    navigate('/time-clock');
  };

  // State management
  const [user, setUser] = useState<Employee | null>(null);
  const [language, setLanguage] = useState<'en' | 'es'>(() => {
    return (localStorage.getItem('language') as 'en' | 'es') || 'en';
  });
  const [jobs, setJobs] = useState<Job[]>([]);
  const [currentJob, setCurrentJob] = useState<JobWithDistance | null>(null);
  const [dailyTicketCount, setDailyTicketCount] = useState<number>(0);
  const [dailyDuplicateCount, setDailyDuplicateCount] = useState<number>(0); // rescans the server flagged today (not counted)

  // Ticket form state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [truckNumber, setTruckNumber] = useState('');
  const [ticketPhoto, setTicketPhoto] = useState<File | null>(null);
  const [truckPhoto, setTruckPhoto] = useState<File | null>(null);
  const [ticketPhotoPreview, setTicketPhotoPreview] = useState<string | null>(null);
  const [truckPhotoPreview, setTruckPhotoPreview] = useState<string | null>(null);

  // Debug state for native upload troubleshooting
  const [debugLogs, setDebugLogs] = useState<string[]>([]);
  const addDebugLog = (msg: string) => {
    const timestamp = new Date().toLocaleTimeString();
    console.log(`[TruckTickets Debug] ${msg}`);
    setDebugLogs(prev => [...prev.slice(-19), `${timestamp}: ${msg}`]);
  };
  const [error, setError] = useState<string | null>(null);
  const [inputKey, setInputKey] = useState(0); // Force input remount on reset
  const [isReady, setIsReady] = useState(false); // Prevent clicks before component is ready
  const [isProcessingPhoto, setIsProcessingPhoto] = useState(false); // Prevent multiple clicks while processing
  const [cameraCooldown, setCameraCooldown] = useState(false); // Prevent rapid camera opens
  const [showCamera, setShowCamera] = useState(false); // Show custom camera for Android
  const [bulkScanCount, setBulkScanCount] = useState<number | null>(null); // Show count after bulk scan

  // Offline support
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingTickets, setPendingTickets] = useState<PendingTicket[]>([]);
  const [queueStats, setQueueStats] = useState({ pending: 0, uploading: 0, failed: 0, completed: 0, total: 0 });

  // Loading states
  const [isInitializing, setIsInitializing] = useState(true);

  // Note: Job mismatch modal is handled globally by GlobalMismatchChecker in App.tsx

  // Track if component has completed initial mount (to avoid redirect race condition)
  const [hasCompletedMount, setHasCompletedMount] = useState(false);

  // Mark component as mounted after a short delay to let location context propagate
  useEffect(() => {
    const timer = setTimeout(() => setHasCompletedMount(true), 500);
    return () => clearTimeout(timer);
  }, []);

  // Listen for page visibility changes (iPad sleep/wake, tab switch) - redirect on wake if stale
  useEffect(() => {
    if (isNativeApp()) return; // Native apps handle this in LocationContext

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('TruckTickets: Page became visible, checking location staleness');
        // Skip when a job was explicitly selected (admin from Job Activity) — they
        // aren't relying on GPS, so a stale location must not bounce them out.
        if (passedJobId) return;
        // Check staleness directly here to avoid race conditions
        if (!location || (Date.now() - location.timestamp) > 5 * 60 * 1000) {
          console.log('TruckTickets: Location stale after wake, redirecting to dashboard');
          navigate('/');
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [location, navigate, passedJobId]);

  // Check for stale location (> 5 minutes old) - PWA only, redirect to dashboard
  // Only redirect after mount completes to avoid race with location context update
  const STALE_THRESHOLD = 5 * 60 * 1000; // 5 minutes
  const isLocationStale = useMemo(() => {
    if (isNativeApp()) return false; // Native apps auto-refresh on resume
    if (!location) return true;
    return (Date.now() - location.timestamp) > STALE_THRESHOLD;
  }, [location]);

  // Redirect to dashboard if location is stale on PWA (no form data to preserve)
  // Wait for mount to complete to avoid race condition with location context update
  useEffect(() => {
    // A job selected from Job Activity (admin) doesn't depend on GPS — never
    // bounce them to the dashboard for a stale/absent location.
    if (passedJobId) return;
    if (isLocationStale && !isNativeApp() && !isInitializing && hasCompletedMount) {
      console.log('TruckTickets: Location stale on mount check, redirecting to dashboard');
      navigate('/');
    }
  }, [isLocationStale, isInitializing, hasCompletedMount, navigate, passedJobId]);

  // Note: Job mismatch detection is handled globally by GlobalMismatchChecker in App.tsx

  // Image compression - EXACT copy from DailyReports (which works perfectly)
  const compressImage = async (file: File): Promise<string> => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const MAX_WIDTH = 1200;  // 1200px is plenty for OCR on 4x6" tickets
          const MAX_HEIGHT = 1200; // Reduced for faster uploads on slow networks

          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height;
              height = MAX_HEIGHT;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0, width, height);

          resolve(canvas.toDataURL('image/jpeg', 0.75)); // 75% quality - good for OCR, faster uploads
        };
        img.src = e.target?.result as string;
      };
      reader.readAsDataURL(file);
    });
  };

  // No cleanup needed for base64 previews (garbage collected automatically)

  // Update queue stats (sync is now handled app-wide in App.tsx)
  useEffect(() => {
    const updateStats = async () => {
      const stats = await uploadQueueService.getQueueStats();
      setQueueStats(stats);
    };
    updateStats();
    const statsInterval = setInterval(updateStats, 5000);

    // Mark component as ready after initial load
    setIsReady(true);

    return () => {
      clearInterval(statsInterval);
      // Don't stop sync here - it's managed app-wide now
    };
  }, []);

  const t = translations[language];

  // Styles matching the app's design
  // Responsive maxWidth: larger on tablets/iPad, standard on mobile
  const isTablet = window.innerWidth >= 768;
  const contentMaxWidth = isTablet ? '700px' : '400px';

  const styles = {
    container: {
      padding: '20px',
      maxWidth: '100vw',
      backgroundColor: '#f8fafc',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    maxWidth: {
      maxWidth: contentMaxWidth,
      margin: '0 auto'
    },
    header: {
      backgroundColor: '#ea580c',
      color: 'white',
      padding: '16px',
      borderTopLeftRadius: '12px',
      borderTopRightRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
    },
    headerTitle: {
      fontSize: '20px',
      fontWeight: '600',
      flex: 1
    },
    headerStatusIcon: {
      marginLeft: 'auto'
    },
    jobInfo: {
      backgroundColor: 'white',
      borderLeft: '4px solid #22c55e',
      padding: '16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
    },
    jobDetails: {
      flex: 1
    },
    jobName: {
      fontSize: '16px',
      fontWeight: '500',
      color: '#1e293b',
      marginBottom: '4px'
    },
    jobNumber: {
      fontSize: '14px',
      color: '#64748b',
      marginBottom: '4px'
    },
    jobDistance: {
      fontSize: '14px',
      color: '#22c55e',
      fontWeight: '500'
    },
    countContainer: {
      textAlign: 'center' as const
    },
    countNumber: {
      fontSize: '24px',
      fontWeight: '700',
      color: '#ea580c'
    },
    countLabel: {
      fontSize: '12px',
      color: '#64748b'
    },
    offlineBanner: {
      backgroundColor: '#fef3c7',
      border: '1px solid #f59e0b',
      color: '#92400e',
      padding: '12px 16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      fontSize: '14px'
    },
    syncButton: {
      backgroundColor: '#d97706',
      color: 'white',
      padding: '4px 12px',
      borderRadius: '6px',
      border: 'none',
      fontSize: '12px',
      cursor: 'pointer'
    },
    uploadQueueBanner: {
      backgroundColor: '#dbeafe',
      border: '1px solid #3b82f6',
      color: '#1e40af',
      padding: '10px 16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      fontSize: '14px',
      gap: '8px'
    },
    uploadQueueSpinner: {
      width: '16px',
      height: '16px',
      border: '2px solid #3b82f6',
      borderTopColor: 'transparent',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    },
    content: {
      backgroundColor: 'white',
      padding: '24px',
      borderBottomLeftRadius: '12px',
      borderBottomRightRadius: '12px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
      minHeight: '400px'
    },
    errorMessage: {
      backgroundColor: '#fef2f2',
      border: '1px solid #fca5a5',
      color: '#dc2626',
      padding: '12px 16px',
      borderRadius: '8px',
      marginBottom: '16px',
      fontSize: '14px'
    },
    formSection: {
      marginBottom: '24px'
    },
    label: {
      display: 'block',
      fontSize: isTablet ? '24px' : '18px',
      fontWeight: '500',
      color: '#374151',
      marginBottom: '16px',
      textAlign: 'center' as const
    },
    numberDisplay: {
      backgroundColor: '#f8fafc',
      padding: isTablet ? '24px' : '16px',
      borderRadius: '8px',
      marginBottom: '24px',
      border: '2px solid #e2e8f0'
    },
    numberDisplayText: {
      fontSize: isTablet ? '36px' : '28px',
      fontFamily: 'monospace',
      fontWeight: '700',
      textAlign: 'center' as const,
      color: '#1e293b',
      minHeight: isTablet ? '60px' : '48px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    numberPad: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: isTablet ? '16px' : '12px',
      maxWidth: isTablet ? '450px' : '300px',
      margin: '0 auto 24px auto'
    },
    numberButton: {
      backgroundColor: '#3b82f6',
      color: 'white',
      fontSize: isTablet ? '32px' : '24px',
      fontWeight: '700',
      padding: isTablet ? '24px' : '16px',
      borderRadius: '8px',
      border: 'none',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      minHeight: '60px'
    },
    clearButton: {
      backgroundColor: '#6b7280',
      color: 'white',
      fontSize: '16px',
      fontWeight: '700',
      padding: '16px',
      borderRadius: '8px',
      border: 'none',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      minHeight: '60px'
    },
    nextButton: {
      width: '100%',
      backgroundColor: '#22c55e',
      color: 'white',
      padding: isTablet ? '20px' : '16px',
      borderRadius: '8px',
      border: 'none',
      fontSize: isTablet ? '22px' : '18px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s ease'
    },
    nextButtonDisabled: {
      width: '100%',
      backgroundColor: '#d1d5db',
      color: '#9ca3af',
      padding: isTablet ? '20px' : '16px',
      borderRadius: '8px',
      border: 'none',
      fontSize: isTablet ? '22px' : '18px',
      fontWeight: '600',
      cursor: 'not-allowed'
    },
    cameraContainer: {
      textAlign: 'center' as const,
      marginBottom: '24px'
    },
    cameraPlaceholder: {
      backgroundColor: '#f8fafc',
      border: '2px dashed #d1d5db',
      borderRadius: '12px',
      padding: '32px',
      marginBottom: '24px'
    },
    cameraIcon: {
      margin: '0 auto 16px auto',
      color: '#9ca3af'
    },
    cameraLabel: {
      fontSize: '18px',
      fontWeight: '500',
      color: '#374151',
      marginBottom: '16px'
    },
    cameraButton: {
      backgroundColor: '#22c55e',
      color: 'white',
      padding: isTablet ? '16px 32px' : '12px 24px',
      borderRadius: '8px',
      border: 'none',
      fontSize: isTablet ? '20px' : '16px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      transition: 'all 0.2s ease'
    },
    buttonGroup: {
      display: 'flex',
      gap: '12px',
      justifyContent: 'center'
    },
    backButton: {
      backgroundColor: '#6b7280',
      color: 'white',
      padding: isTablet ? '16px 32px' : '12px 24px',
      borderRadius: '8px',
      border: 'none',
      fontSize: isTablet ? '20px' : '16px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s ease'
    },
    skipButton: {
      backgroundColor: '#3b82f6',
      color: 'white',
      padding: isTablet ? '16px 32px' : '12px 24px',
      borderRadius: '8px',
      border: 'none',
      fontSize: isTablet ? '20px' : '16px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s ease'
    },
    statusContainer: {
      textAlign: 'center' as const,
      padding: '32px'
    },
    statusIcon: {
      margin: '0 auto 16px auto'
    },
    statusMessage: {
      fontSize: '18px',
      fontWeight: '600',
      marginBottom: '16px'
    },
    successMessage: {
      color: '#22c55e'
    },
    hiddenInput: {
      display: 'none'
    },
    noJobContainer: {
      textAlign: 'center' as const,
      padding: '24px'
    },
    noJobIcon: {
      margin: '0 auto 16px auto',
      color: '#9ca3af'
    },
    noJobMessage: {
      fontSize: '18px',
      color: '#374151',
      marginBottom: '16px',
      lineHeight: '1.5'
    },
    nearestJobText: {
      fontSize: '14px',
      color: '#64748b'
    },
    // New single-page layout styles
    scrollContainer: {
      maxHeight: '80vh',
      overflowY: 'auto' as const,
      padding: '20px'
    },
    sectionTitle: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#374151',
      marginBottom: '16px',
      paddingBottom: '8px',
      borderBottom: '2px solid #e5e7eb'
    },
    photoSection: {
      marginBottom: '24px'
    },
    photoPreviewContainer: {
      position: 'relative' as const,
      marginBottom: '16px'
    },
    photoPreview: {
      width: '100%',
      maxWidth: '300px',
      height: '200px',
      objectFit: 'cover' as const,
      borderRadius: '8px',
      border: '2px solid #e5e7eb',
      display: 'block',
      margin: '0 auto'
    },
    photoPlaceholder: {
      width: '100%',
      maxWidth: '300px',
      height: '200px',
      backgroundColor: '#f3f4f6',
      border: '2px dashed #d1d5db',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto',
      color: '#9ca3af',
      fontSize: '14px',
      flexDirection: 'column' as const,
      gap: '8px'
    },
    removePhotoButton: {
      position: 'absolute' as const,
      top: '8px',
      right: 'calc(50% - 150px)',
      backgroundColor: '#dc2626',
      color: 'white',
      border: 'none',
      borderRadius: '50%',
      width: '24px',
      height: '24px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '12px'
    },
    submitSection: {
      marginTop: '32px',
      padding: '20px',
      backgroundColor: '#f9fafb',
      borderRadius: '8px',
      border: '1px solid #e5e7eb'
    },
    submitButton: {
      width: '100%',
      backgroundColor: '#22c55e',
      color: 'white',
      padding: '16px',
      borderRadius: '8px',
      border: 'none',
      fontSize: '18px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      transition: 'all 0.2s ease'
    },
    submitButtonDisabled: {
      width: '100%',
      backgroundColor: '#9ca3af',
      color: 'white',
      padding: '16px',
      borderRadius: '8px',
      border: 'none',
      fontSize: '18px',
      fontWeight: '600',
      cursor: 'not-allowed',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px'
    },
    successOverlay: {
      position: 'fixed' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    },
    successCard: {
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '32px',
      textAlign: 'center' as const,
      maxWidth: '400px',
      width: '100%'
    }
  };

  // Distance calculation (same as ClockInOut)
  const calculateDistance = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lng2 - lng1) * Math.PI / 180;

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  };

  const metersToMiles = (meters: number): number => {
    return meters * 0.000621371;
  };

  // Check if user is within job geofence
  const isUserWithinJobGeofence = useCallback((job: Job, userLat: number, userLng: number): boolean => {
    if (job.geofence_type === 'polygon' && job.geofence_data?.points && job.geofence_data.points.length > 0) {
      return isInsidePolygon(userLat, userLng, job.geofence_data.points);
    } else if (job.geofence_type === 'circle' && job.geofence_data?.radius) {
      const distance = calculateDistance(userLat, userLng, job.lat, job.lng);
      return distance <= job.geofence_data.radius;
    } else {
      const distance = calculateDistance(userLat, userLng, job.lat, job.lng);
      return distance <= 100; // Default 100m radius
    }
  }, []);

  // Polygon geofence check
  const isInsidePolygon = (userLat: number, userLng: number, points: Array<{ lat: number; lng: number }>): boolean => {
    let inside = false;
    for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
      const xi = points[i].lat, yi = points[i].lng;
      const xj = points[j].lat, yj = points[j].lng;

      const intersect = ((yi > userLng) !== (yj > userLng))
          && (userLat < (xj - xi) * (userLng - yi) / (yj - yi) + xi);
      if (intersect) inside = !inside;
    }
    return inside;
  };

  // Load user data and listen for language changes
  useEffect(() => {
    const userData = localStorage.getItem('bfa_user');
    const langData = localStorage.getItem('language');

    if (userData) {
      setUser(JSON.parse(userData));
    }
    if (langData === 'en' || langData === 'es') {
      setLanguage(langData);
    }

    // Listen for language changes
    const handleLanguageChange = (event: Event) => {
      const newLang = (event as CustomEvent).detail;
      if (newLang === 'en' || newLang === 'es') {
        setLanguage(newLang);
      }
    };

    window.addEventListener('languageChange', handleLanguageChange);

    return () => {
      window.removeEventListener('languageChange', handleLanguageChange);
    };
  }, []);


  // Load jobs and check clocked-in status
  useEffect(() => {
    if (user) {
      const initializeData = async () => {
        try {
          // Load jobs first (need jobs list to match clock-in job)
          const jobsList = await loadJobs();

          // Check if a job was passed via navigation (from Job Activity page)
          if (passedJobId) {
            const passedJob = jobsList.find((job: Job) => job.job_id === passedJobId);
            if (passedJob) {
              console.log('[TruckTickets] Job passed via navigation:', passedJob.job_name);
              setCurrentJob({
                ...passedJob,
                distance: 'Selected',
                distanceValue: 0,
                distanceInMiles: 0,
                isWithinGeofence: true
              });
              loadDailyTicketCount(passedJob.job_id);
              setIsInitializing(false);
              loadPendingTickets();
              return;
            }
          }

          // Check if user is clocked in to a job first
          const isClockedIn = await checkClockedInJob(jobsList);

          // For admins not clocked in: Auto-detect job via GPS
          if (!isClockedIn && auth?.user?.role === 'admin') {
            console.log('[TruckTickets] Admin not clocked in, auto-detecting job via GPS...');
            await autoDetectJobFromGPS(jobsList);
          }

          // For workers/foremen not clocked in: Show "Please clock in" message
          setIsInitializing(false);

          loadPendingTickets();
        } catch (err) {
          console.error('Error initializing data:', err);
          setIsInitializing(false);
        }
      };
      initializeData();
    }
  }, [user, passedJobId]);

  // Online/offline detection
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      syncPendingTickets();
    };
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const checkClockedInJob = async (jobsList: Job[]): Promise<boolean> => {
    try {
      // Check if user is clocked in to a job
      const response = await fetch(`${API_BASE_URL}/api/time/current`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
        }
      });
      const data = await response.json();

      if (data.success && data.data?.job_id) {
        console.log('User is clocked into job:', data.data.job_name);
        // Find the job in the jobs list and set it as current
        const clockedJob = jobsList.find((job: Job) => job.job_id === data.data.job_id);
        if (clockedJob) {
          setCurrentJob({
            ...clockedJob,
            distance: 'Clocked In',
            distanceValue: 0,
            distanceInMiles: 0,
            isWithinGeofence: true
          });
          // Note: Job mismatch detection is handled globally by GlobalMismatchChecker
          // Load the daily ticket count for this job
          loadDailyTicketCount(clockedJob.job_id);
          return true;
        }
      }
      // Not clocked in
      return false;
    } catch (err) {
      console.error('Error checking clock status:', err);
      return false;
    }
  };

  /**
   * Auto-detect job based on GPS location (for admins not clocked in)
   * Finds the job the user is within geofence of, or nearest job
   */
  const autoDetectJobFromGPS = async (jobsList: Job[]): Promise<boolean> => {
    try {
      // Get fresh location
      const loc = await forceRefreshLocation();
      if (!loc) {
        console.log('[TruckTickets] Could not get GPS location for auto-detect');
        return false;
      }

      console.log('[TruckTickets] Got GPS location:', loc.latitude, loc.longitude);

      // Helper to check if user is within job geofence
      const isWithinGeofence = (job: Job): boolean => {
        if (job.geofence_type === 'circle' && job.geofence_data?.radius) {
          return geofenceService.isInsideCircle(
            loc.latitude,
            loc.longitude,
            job.lat,
            job.lng,
            job.geofence_data.radius
          );
        } else if (job.geofence_type === 'polygon' && job.geofence_data?.points) {
          return geofenceService.isInsidePolygon(
            loc.latitude,
            loc.longitude,
            job.geofence_data.points
          );
        }
        // Default: within 100m
        const distance = geofenceService.calculateDistance(loc.latitude, loc.longitude, job.lat, job.lng);
        return distance <= 100;
      };

      // Calculate distances for all jobs (exclude shop jobs)
      const jobsWithDistance = jobsList
        .filter(job => !SHOP_JOB_IDS.includes(job.job_id))
        .map(job => {
          const distanceInMeters = geofenceService.calculateDistance(
            loc.latitude,
            loc.longitude,
            job.lat,
            job.lng
          );
          const distanceInMiles = geofenceService.metersToMiles(distanceInMeters);
          const withinGeofence = isWithinGeofence(job);

          return {
            ...job,
            distance: withinGeofence ? 'At Location' : `${distanceInMiles.toFixed(2)} miles away`,
            distanceValue: distanceInMeters,
            distanceInMiles,
            isWithinGeofence: withinGeofence
          };
        })
        .sort((a, b) => a.distanceValue - b.distanceValue);

      // First priority: job user is within geofence of
      const jobAtLocation = jobsWithDistance.find(job => job.isWithinGeofence);
      if (jobAtLocation) {
        console.log('[TruckTickets] Admin is at job site:', jobAtLocation.job_name);
        setCurrentJob(jobAtLocation);
        loadDailyTicketCount(jobAtLocation.job_id);
        return true;
      }

      // Second priority: nearest job within 0.5 miles
      const nearestJob = jobsWithDistance[0];
      if (nearestJob && nearestJob.distanceInMiles <= NEARBY_RADIUS_MILES) {
        console.log('[TruckTickets] Admin near job site:', nearestJob.job_name, `(${nearestJob.distanceInMiles.toFixed(2)} miles)`);
        setCurrentJob(nearestJob);
        loadDailyTicketCount(nearestJob.job_id);
        return true;
      }

      console.log('[TruckTickets] No nearby job found for admin, will show JobSelector');
      return false;
    } catch (err) {
      console.error('[TruckTickets] Error auto-detecting job:', err);
      return false;
    }
  };

  const loadJobs = async (): Promise<Job[]> => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/jobs`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Include all jobs (including shop jobs)
        setJobs(data.data);
        return data.data || [];
      }
      return [];
    } catch (error) {
      console.error('Error loading jobs:', error);
      return [];
    }
  };


  const loadDailyTicketCount = async (jobId: number) => {
    try {
      // Use local date for truck tickets (backend compares in Eastern timezone)
      const now = new Date();
      const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      const url = `${API_BASE_URL}/api/trucks?job_id=${jobId}&start_date=${today}&end_date=${today}`;
      console.log('Loading daily ticket count from:', url);
      console.log('Today date:', today);

      // Also try without date filter to see all tickets for this job
      const allTicketsUrl = `${API_BASE_URL}/api/trucks?job_id=${jobId}`;
      console.log('Loading all tickets for job from:', allTicketsUrl);

      const [dailyResponse, allResponse] = await Promise.all([
        fetch(url, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
          }
        }),
        fetch(allTicketsUrl, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
          }
        })
      ]);

      if (dailyResponse.ok) {
        const dailyData = await dailyResponse.json();
        console.log('Daily tickets data:', dailyData);
        console.log('Setting daily count to:', dailyData.data.length, 'duplicates excluded:', dailyData.duplicates_excluded);
        setDailyTicketCount(dailyData.data.length);
        setDailyDuplicateCount(Number(dailyData.duplicates_excluded) || 0);
      } else {
        console.error('Failed to load daily ticket count:', dailyResponse.status, dailyResponse.statusText);
      }

      if (allResponse.ok) {
        const allData = await allResponse.json();
        console.log('All tickets for job:', allData);
        console.log('Total tickets for this job:', allData.data.length);
        if (allData.data.length > 0) {
          console.log('Sample ticket dates:', allData.data.slice(0, 3).map(t => t.created_at));
        }
      }
    } catch (error) {
      console.error('Error loading daily ticket count:', error);
    }
  };

  // OCR (and the server's duplicate-scan check) finishes seconds to a couple of minutes after upload,
  // so re-read the count a few times after a scan batch — that's when "N duplicate scans ignored" appears.
  const scheduleCountRefresh = (jobId: number) => {
    [20000, 60000, 150000].forEach(ms => setTimeout(() => loadDailyTicketCount(jobId), ms));
  };

  const loadPendingTickets = () => {
    const stored = localStorage.getItem('bfa_pending_truck_tickets');
    if (stored) {
      setPendingTickets(JSON.parse(stored));
    }
  };

  const savePendingTicket = (ticket: PendingTicket) => {
    const updated = [...pendingTickets, ticket];
    setPendingTickets(updated);
    localStorage.setItem('bfa_pending_truck_tickets', JSON.stringify(updated));
  };

  const removePendingTicket = (ticketId: string) => {
    const updated = pendingTickets.filter(t => t.id !== ticketId);
    setPendingTickets(updated);
    localStorage.setItem('bfa_pending_truck_tickets', JSON.stringify(updated));
  };

  const syncPendingTickets = async () => {
    if (!isOnline || pendingTickets.length === 0) return;

    for (const ticket of pendingTickets) {
      try {
        await submitTicketToServer(
          ticket.jobId,
          ticket.truckNumber,
          ticket.ticketPhoto,
          ticket.truckPhoto
        );
        removePendingTicket(ticket.id);
      } catch (error) {
        console.error('Error syncing ticket:', error);
        break; // Stop syncing on first error
      }
    }
  };

  const submitTicketToServer = async (
    jobId: number,
    truckNum: string,
    ticketPhotoFile: File,
    truckPhotoFile?: File
  ) => {
    const formData = new FormData();
    formData.append('job_id', jobId.toString());
    formData.append('truck_number', truckNum);
    formData.append('ticket_photo', ticketPhotoFile);

    // Add required fields with default values for simple truck tickets
    formData.append('ticket_number', `TICK-${Date.now()}`);
    formData.append('po_number', '');
    formData.append('truck_company', '');
    formData.append('hauler_id', '');
    formData.append('hauler_name', '');
    formData.append('material_type', '');
    formData.append('pit_location', '');
    formData.append('weight', '0');
    formData.append('quantity', '0');
    formData.append('time_in_pit', new Date().toISOString());
    formData.append('time_out_pit', new Date().toISOString());

    if (truckPhotoFile) {
      formData.append('truck_photo', truckPhotoFile);
    }

    const response = await fetch(`${API_BASE_URL}/api/trucks`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
      },
      body: formData
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Server error:', response.status, errorText);
      throw new Error(`Failed to submit ticket: ${response.status} - ${errorText}`);
    }

    return response.json();
  };

  const handleSubmit = async () => {
    // Only require ticket photo now
    if (!currentJob || !ticketPhoto) {
      setError(t.ticketRequired);
      return;
    }

    // Get user ID from storage (fresh check at submit time)
    const userData = localStorage.getItem('bfa_user');
    const employeeId = userData ? JSON.parse(userData).employee_id : 0;

    setIsSubmitting(true);
    setError(null);

    try {
      const files: { file: Blob; filename: string; fieldName: string }[] = [
        { file: ticketPhoto, filename: ticketPhoto.name, fieldName: 'ticket_photo' }
      ];

      // truckPhoto is commented out, but queue still supports it
      // if (truckPhoto) {
      //   files.push({ file: truckPhoto, filename: truckPhoto.name, fieldName: 'truck_photo' });
      // }

      // Add to queue (saves immediately, uploads in background)
      await uploadQueueService.addToQueue(
        'truck_ticket',
        {
          job_id: currentJob.job_id,
          truck_number: truckNumber || '', // Optional truck number
          ticket_number: `TICK-${Date.now()}`,
          po_number: '',
          truck_company: '',
          hauler_id: '',
          hauler_name: '',
          material_type: '',
          pit_location: '',
          weight: '0',
          quantity: '0',
          time_received: new Date().toISOString()
        },
        files,
        employeeId,
        currentJob.job_id
      );

      setShowSuccess(true);

      // Reset form after success
      setTimeout(() => {
        resetForm();
        loadDailyTicketCount(currentJob.job_id);
        scheduleCountRefresh(currentJob.job_id);
        window.scrollTo(0, 0);
      }, 2000);

    } catch (error) {
      console.error('Error queueing ticket:', error);
      setError('Failed to queue ticket. Please try again.');
    }

    setIsSubmitting(false);
  };

  const resetForm = () => {
    // No cleanup needed for base64 previews (garbage collected automatically)
    setTruckNumber('');
    setTicketPhoto(null);
    setTicketPhotoPreview(null);
    setIsSubmitting(false);
    setShowSuccess(false);
    setError(null);
    setInputKey(prev => prev + 1); // Force camera input to remount
  };

  // Native: Scan with VisionKit/ML Kit (single or multiple tickets, queued individually)
  // pageLimit: 1 for single scan, 25 for bulk scan
  const handleNativeScan = async (pageLimit: number = 1) => {
    if (cameraCooldown || !isReady || !currentJob) return;

    const isBulk = pageLimit > 1;

    try {
      setCameraCooldown(true);
      setError(null);
      if (isBulk) setBulkScanCount(null);

      // Request camera permission on Android before scanning
      if (isNativeAndroid()) {
        console.log('[NativeScan] Requesting camera permission...');
        const permResult = await CapCamera.requestPermissions({ permissions: ['camera'] });
        console.log('[NativeScan] Camera permission result:', permResult);

        if (permResult.camera !== 'granted') {
          setError('Camera permission required. Please allow camera access in Settings.');
          setCameraCooldown(false);
          return;
        }
      }

      console.log(`[NativeScan] Starting scan with pageLimit: ${pageLimit}...`);
      const result = await documentScannerService.scanBulk(pageLimit);

      if (result.cancelled) {
        setCameraCooldown(false);
        return;
      }

      addDebugLog(`Scanner returned: ${result.images.length} images, cancelled=${result.cancelled}`);
      console.log('[NativeScan] Result from scanner:', {
        imageCount: result.images.length,
        cancelled: result.cancelled,
        count: result.count
      });

      if (result.images.length > 0) {
        // Get user ID from storage
        const userData = localStorage.getItem('bfa_user');
        const employeeId = userData ? JSON.parse(userData).employee_id : 0;
        addDebugLog(`Employee ID: ${employeeId}, Job ID: ${currentJob.job_id}`);

        const now = new Date();
        const dateStr = `${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}-${String(now.getFullYear()).slice(-2)}`;

        console.log(`[NativeScan] Processing ${result.images.length} images...`);
        addDebugLog(`Processing ${result.images.length} images...`);

        // Queue each scanned image individually
        let queuedCount = 0;
        for (let i = 0; i < result.images.length; i++) {
          try {
            addDebugLog(`Image ${i + 1}: Resizing...`);
            // Resize image before upload (max 1200px, 80% quality - optimized for truck tickets)
            const resizedBase64 = await documentScannerService.resizeBase64Image(result.images[i], 1200, 0.80);
            addDebugLog(`Image ${i + 1}: Resized, base64 len=${resizedBase64.length}`);

            const file = documentScannerService.base64ToFile(
              resizedBase64,
              `ticket-${dateStr}-${Date.now()}-${i + 1}.jpg`
            );
            addDebugLog(`Image ${i + 1}: File created, size=${file.size}`);

            if (file.size === 0) {
              addDebugLog(`Image ${i + 1}: SKIP - 0 byte file`);
              console.warn(`[NativeScan] Skipping image ${i + 1}: 0 byte file`);
              continue;
            }

            addDebugLog(`Image ${i + 1}: Calling addToQueue...`);
            const queueResult = await uploadQueueService.addToQueue(
              'truck_ticket',
              {
                job_id: currentJob.job_id,
                truck_number: '',
                ticket_number: `TICK-${Date.now()}-${i + 1}`,
                po_number: '',
                truck_company: '',
                hauler_id: '',
                hauler_name: '',
                material_type: '',
                pit_location: '',
                weight: '0',
                quantity: '0',
                time_received: new Date().toISOString(),
                scanner_type: 'visionkit' // Skip server-side OpenCV - already cropped
              },
              [{ file, filename: file.name, fieldName: 'ticket_photo', base64: resizedBase64 }],
              employeeId,
              currentJob.job_id
            );
            addDebugLog(`Image ${i + 1}: Queue result=${queueResult}`);
            queuedCount++;
            console.log(`[NativeScan] Image ${i + 1} queued successfully`);

            // Small delay between queues to ensure unique timestamps
            await new Promise(resolve => setTimeout(resolve, 100));
          } catch (imgError: any) {
            addDebugLog(`Image ${i + 1}: ERROR - ${imgError?.message || imgError}`);
            console.error(`[NativeScan] Error processing image ${i + 1}:`, imgError);
          }
        }
        addDebugLog(`Total queued: ${queuedCount}/${result.images.length}`);

        console.log(`[NativeScan] Total queued: ${queuedCount}/${result.images.length}`);

        // Show success overlay (bulk shows count, single shows quick success)
        if (isBulk) {
          setBulkScanCount(queuedCount);
          setTimeout(() => {
            setBulkScanCount(null);
            loadDailyTicketCount(currentJob.job_id);
            scheduleCountRefresh(currentJob.job_id);
          }, 3000);
        } else {
          // Single scan - show quick success and refresh count
          setShowSuccess(true);
          setTimeout(() => {
            setShowSuccess(false);
            loadDailyTicketCount(currentJob.job_id);
            scheduleCountRefresh(currentJob.job_id);
          }, 1500);
        }
      }

      setTimeout(() => setCameraCooldown(false), 500);
    } catch (err: any) {
      console.error('Native scan error:', err);
      const errorMsg = (err?.message || 'Unknown error');
      const errorLower = errorMsg.toLowerCase();

      if (errorLower.includes('canceled') || errorLower.includes('cancelled')) {
        // User cancelled - not an error
        setError(null);
      } else if (errorLower.includes('module') || errorLower.includes('mlkit') ||
                 errorLower.includes('play services') || errorLower.includes('not available') ||
                 errorLower.includes('not installed')) {
        // ML Kit module not ready - prompt user
        setError('Scanner downloading... Please wait and try again.');
      } else if (errorLower.includes('conversion') || errorLower.includes('convert') || errorLower.includes('file')) {
        setError('Failed to save scanned images. Please try again.');
      } else {
        // Show actual error for debugging
        setError(`Error: ${errorMsg}`);
        console.error('Full error object:', JSON.stringify(err, null, 2));
      }
      setCameraCooldown(false);
    }
  };

  // Convenience wrapper for bulk scan (25 pages) - used by header button
  const handleBulkScan = () => handleNativeScan(25);

  // Camera handler - uses native scanner on iOS/Android, PWA camera otherwise
  const handleTakePhoto = async () => {
    if (cameraCooldown || !isReady || !currentJob) return;

    // Native iOS: Use VisionKit with 25 page limit (multi-scan)
    if (isNativeIOS()) {
      await handleNativeScan(25);
      return;
    }

    // Native Android: Use ML Kit with 1 page limit (single scan)
    // Bulk scan available via header button
    if (isNativeAndroid()) {
      await handleNativeScan(1);
      return;
    }

    // PWA iOS: Use native camera file input (no permission prompts)
    if (isIOS()) {
      try {
        setCameraCooldown(true);
        await openNativeCamera(async (file) => {
          if (file && file instanceof File) {
            const previewUrl = await fileToDataUrl(file);
            setTicketPhoto(file);
            setTicketPhotoPreview(previewUrl);
            setError(null);

            // Auto-submit after photo is captured (needs server-side crop)
            await submitTicketWithPhoto(file, 'camera');
          }
        });
        setTimeout(() => setCameraCooldown(false), 1000);
      } catch (err) {
        console.error('Error capturing photo:', err);
        setError('Failed to capture photo. Please try again.');
        setCameraCooldown(false);
      }
    } else {
      // Android: Use custom camera component
      setShowCamera(true);
    }
  };

  const handleCameraCapture = async (imageDataUrl: string) => {
    if (!currentJob) return;

    setShowCamera(false);

    try {
      // Convert base64 to blob
      const response = await fetch(imageDataUrl);
      const blob = await response.blob();
      // Format filename as MM-DD-YY
      const now = new Date();
      const dateStr = `${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}-${String(now.getFullYear()).slice(-2)}`;
      const file = new File([blob], `ticket-${dateStr}-${Date.now()}.jpg`, { type: 'image/jpeg' });

      const previewUrl = await fileToDataUrl(file);
      setTicketPhoto(file);
      setTicketPhotoPreview(previewUrl);
      setError(null);

      // Auto-submit after photo is captured (needs server-side crop)
      await submitTicketWithPhoto(file, 'camera');

      // Cooldown
      setCameraCooldown(true);
      setTimeout(() => setCameraCooldown(false), 1000);
    } catch (err) {
      console.error('Error processing camera capture:', err);
      setError('Failed to process photo. Please try again.');
      setCameraCooldown(false);
    }
  };

  const handleCameraClose = () => {
    setShowCamera(false);
    setCameraCooldown(true);
    setTimeout(() => setCameraCooldown(false), 500);
  };

  const submitTicketWithPhoto = async (photoFile: File, scannerType: 'visionkit' | 'camera' = 'camera') => {
    if (!currentJob) return;

    // Get user ID from storage (fresh check at submit time)
    const userData = localStorage.getItem('bfa_user');
    const employeeId = userData ? JSON.parse(userData).employee_id : 0;

    setIsSubmitting(true);
    setError(null);

    try {
      const files: { file: Blob; filename: string; fieldName: string }[] = [
        { file: photoFile, filename: photoFile.name, fieldName: 'ticket_photo' }
      ];

      // Add to queue (saves immediately, uploads in background)
      // scanner_type tells server whether to run OpenCV cropping
      // 'visionkit' = already cropped on device, skip server crop
      // 'camera' = raw photo, needs server-side cropping
      await uploadQueueService.addToQueue(
        'truck_ticket',
        {
          job_id: currentJob.job_id,
          truck_number: truckNumber || '', // Optional truck number
          ticket_number: `TICK-${Date.now()}`,
          po_number: '',
          truck_company: '',
          hauler_id: '',
          hauler_name: '',
          material_type: '',
          pit_location: '',
          weight: '0',
          quantity: '0',
          time_received: new Date().toISOString(),
          scanner_type: scannerType
        },
        files,
        employeeId,
        currentJob.job_id
      );

      setShowSuccess(true);

      // Reset form after success
      setTimeout(() => {
        resetForm();
        loadDailyTicketCount(currentJob.job_id);
        scheduleCountRefresh(currentJob.job_id);
        window.scrollTo(0, 0);
      }, 2000);

    } catch (error) {
      console.error('Error queueing ticket:', error);
      setError('Failed to queue ticket. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Number pad component
  const NumberPad = () => (
    <div style={styles.numberPad}>
      {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
        <button
          key={num}
          onClick={() => setTruckNumber(prev => prev + num.toString())}
          style={styles.numberButton}
          onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
          onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}
        >
          {num}
        </button>
      ))}
      <button
        onClick={() => setTruckNumber('')}
        style={styles.clearButton}
        onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#4b5563'}
        onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#6b7280'}
      >
        {t.clear}
      </button>
      <button
        onClick={() => setTruckNumber(prev => prev + '0')}
        style={styles.numberButton}
        onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
        onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}
      >
        0
      </button>
      <button
        onClick={() => setTruckNumber(prev => prev.slice(0, -1))}
        style={styles.clearButton}
        onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#4b5563'}
        onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#6b7280'}
      >
        ⌫
      </button>
    </div>
  );

  // Loading overlay while initializing - MUST come before other checks
  if (isInitializing) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.95)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '20px'
      }}>
        <Loader2
          size={48}
          style={{
            color: 'white',
            animation: 'spin 1s linear infinite'
          }}
        />
        <div style={{
          color: 'white',
          fontSize: 18,
          marginTop: 20,
          fontWeight: '500'
        }}>
          {language === 'en' ? 'Loading...' : 'Cargando...'}
        </div>
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Not clocked in screen - different for admin vs workers
  if (!currentJob) {
    const isAdmin = auth?.user?.role === 'admin';

    return (
      <div style={styles.container}>
        <div style={styles.maxWidth}>
          {/* Header */}
          <div style={styles.header}>
            <Truck size={24} />
            <h1 style={styles.headerTitle}>{t.truckTickets}</h1>
            {!isOnline ? <WifiOff size={20} /> : <Wifi size={20} />}
          </div>

          <div style={styles.content}>
            {isAdmin ? (
              // Admin: Show JobSelector
              <div>
                <div style={{
                  textAlign: 'center' as const,
                  marginBottom: '24px',
                  padding: '16px',
                  backgroundColor: '#f0f9ff',
                  borderRadius: '8px',
                  border: '1px solid #bfdbfe'
                }}>
                  <p style={{ fontSize: '16px', color: '#1e293b', marginBottom: '8px' }}>
                    You are not clocked in
                  </p>
                  <p style={{ fontSize: '14px', color: '#64748b' }}>
                    Please select a job to continue
                  </p>
                </div>
                <JobSelector
                  jobs={jobs}
                  currentJob={currentJob}
                  onJobSelect={(job) => {
                    setCurrentJob({
                      ...job,
                      distance: 'Selected',
                      distanceValue: 0,
                      distanceInMiles: 0,
                      isWithinGeofence: false
                    });
                    loadDailyTicketCount(job.job_id);
                  }}
                  mode="admin"
                  language={language}
                />
              </div>
            ) : (
              // Workers/Foremen: Must clock in first
              <div style={styles.noJobContainer}>
                <MapPin size={48} style={styles.noJobIcon} />
                <p style={styles.noJobMessage}>
                  {language === 'en'
                    ? 'You must be clocked in to submit truck tickets'
                    : 'Debe estar registrado para enviar boletos de camión'}
                </p>
                <button
                  onClick={navigateToTimeClock}
                  style={{
                    ...styles.submitButton,
                    marginTop: '24px',
                    maxWidth: '300px'
                  }}
                >
                  {language === 'en' ? 'Go to Time Clock' : 'Ir al Reloj'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Note: Job mismatch modal is handled globally by GlobalMismatchChecker in App.tsx */}

      <div style={styles.maxWidth}>
        {/* Header */}
        <div style={styles.header}>
          <Truck size={24} />
          <h1 style={styles.headerTitle}>{t.truckTickets}</h1>
          {/* Bulk Scan button - iOS & Android, uses VisionKit/ML Kit */}
          {documentScannerService.isAvailable() && (
            <button
              onClick={handleBulkScan}
              disabled={cameraCooldown || !isReady}
              style={{
                backgroundColor: 'rgba(255,255,255,0.2)',
                color: 'white',
                border: '1px solid rgba(255,255,255,0.4)',
                borderRadius: '6px',
                padding: '6px 12px',
                fontSize: '12px',
                fontWeight: '600',
                cursor: cameraCooldown ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                opacity: cameraCooldown ? 0.5 : 1
              }}
            >
              <Layers size={14} />
              {t.bulkScan}
            </button>
          )}
          <span style={{ fontSize: '10px', opacity: 0.7, marginRight: '8px' }}>v4.2</span>
          {!isOnline ? <WifiOff size={20} /> : <Wifi size={20} />}
        </div>

        {/* Current job info */}
        <div style={styles.jobInfo}>
          <div style={styles.jobDetails}>
            <h2 style={styles.jobName}>{currentJob.job_name}</h2>
            <p style={styles.jobNumber}>#{currentJob.bradanna_job_number}</p>
            <p style={styles.jobDistance}>{currentJob.distance}</p>
          </div>
          <div style={styles.countContainer}>
            <div style={styles.countNumber}>{dailyTicketCount}</div>
            <div style={styles.countLabel}>{t.dailyCount}</div>
            {dailyDuplicateCount > 0 && (
              <div style={{ ...styles.countLabel, color: '#f59e0b', fontWeight: 600 }}>
                {dailyDuplicateCount} {t.duplicatesIgnored}
              </div>
            )}
          </div>
        </div>

        {/* Offline status */}
        {!isOnline && (
          <div style={styles.offlineBanner}>
            <span>
              {t.offline} • {pendingTickets.length} {t.pendingTickets}
            </span>
            {pendingTickets.length > 0 && (
              <button
                onClick={syncPendingTickets}
                style={styles.syncButton}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#b45309'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#d97706'}
              >
                {t.sync}
              </button>
            )}
          </div>
        )}

        {/* Upload queue status - shows when tickets are pending upload */}
        {isOnline && (queueStats.pending > 0 || queueStats.uploading > 0) && (
          <div style={styles.uploadQueueBanner}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={styles.uploadQueueSpinner} />
              <span>
                {queueStats.uploading > 0
                  ? (language === 'es'
                      ? `Subiendo ${queueStats.uploading} boleto${queueStats.uploading > 1 ? 's' : ''}...`
                      : `Uploading ${queueStats.uploading} ticket${queueStats.uploading > 1 ? 's' : ''}...`)
                  : (language === 'es'
                      ? `${queueStats.pending} boleto${queueStats.pending > 1 ? 's' : ''} en cola`
                      : `${queueStats.pending} ticket${queueStats.pending > 1 ? 's' : ''} queued`)}
              </span>
            </div>
            {queueStats.pending > 0 && queueStats.uploading === 0 && (
              <span style={{ fontSize: '12px', opacity: 0.8 }}>
                {language === 'es' ? 'Esperando conexión...' : 'Waiting for connection...'}
              </span>
            )}
          </div>
        )}

        {/* Failed uploads indicator */}
        {queueStats.failed > 0 && (
          <div style={{ ...styles.offlineBanner, backgroundColor: '#fee2e2', borderColor: '#ef4444', color: '#991b1b' }}>
            <span>
              {language === 'es'
                ? `${queueStats.failed} boleto${queueStats.failed > 1 ? 's' : ''} fallido${queueStats.failed > 1 ? 's' : ''}`
                : `${queueStats.failed} ticket${queueStats.failed > 1 ? 's' : ''} failed`}
            </span>
            <button
              onClick={() => uploadQueueService.processQueue()}
              style={{ ...styles.syncButton, backgroundColor: '#dc2626' }}
            >
              {language === 'es' ? 'Reintentar' : 'Retry'}
            </button>
          </div>
        )}

        {/* Single-page content with photo previews */}
        <div style={styles.content}>
          {error && (
            <div style={styles.errorMessage}>
              {error}
            </div>
          )}

          <div style={styles.scrollContainer}>
            {/* Truck Number Section - COMMENTED OUT - Can be restored if needed */}
            {/* <div style={styles.formSection}>
              <h3 style={styles.sectionTitle}>🚛 {t.truckNumber}</h3>
              <div style={styles.numberDisplay}>
                <div style={styles.numberDisplayText}>
                  {truckNumber || t.enterTruckNumber}
                </div>
              </div>
              <NumberPad />
            </div> */}

            {/* Ticket Photo Section */}
            <div style={styles.photoSection}>
              <h3 style={styles.sectionTitle}>📋 {t.ticketPhoto}</h3>

              {/* Same camera UI for all platforms - tap to capture */}
              <div style={styles.photoPreviewContainer}>
                {ticketPhotoPreview ? (
                  <>
                    <img src={ticketPhotoPreview} alt="Ticket preview" style={styles.photoPreview} />
                    <button
                      style={styles.removePhotoButton}
                        onClick={() => {
                          setTicketPhoto(null);
                          setTicketPhotoPreview(null);
                        }}
                      >
                        ×
                      </button>
                    </>
                  ) : (
                    <div
                      onClick={handleTakePhoto}
                      style={{
                        ...styles.photoPlaceholder,
                        cursor: (isReady && !cameraCooldown) ? 'pointer' : 'wait',
                        opacity: (isReady && !cameraCooldown) ? 1 : 0.6
                      }}
                    >
                      <Camera size={32} />
                      <span>
                        {!isReady ? t.loading : cameraCooldown ? t.pleaseWait : t.tapToTakePhoto}
                      </span>
                    </div>
                  )}
                </div>
            </div>
          </div>
        </div>

        {/* Success Overlay */}
        {showSuccess && (
          <div style={styles.successOverlay}>
            <div style={styles.successCard}>
              <Check size={64} style={{ color: '#22c55e', margin: '0 auto 16px auto' }} />
              <h2 style={{ margin: '0 0 8px 0', color: '#22c55e' }}>{t.success}</h2>
              <p style={{ margin: '0 0 24px 0', color: '#6b7280' }}>
                {t.ticketSubmitted}
              </p>
              <button
                onClick={resetForm}
                style={styles.submitButton}
              >
                Submit Another Ticket
              </button>
            </div>
          </div>
        )}

        {/* Bulk Scan Success Overlay */}
        {bulkScanCount !== null && (
          <div style={styles.successOverlay}>
            <div style={styles.successCard}>
              <Layers size={64} style={{ color: '#3b82f6', margin: '0 auto 16px auto' }} />
              <h2 style={{ margin: '0 0 8px 0', color: '#3b82f6' }}>
                {bulkScanCount} {language === 'en' ? 'Tickets Queued!' : 'Boletos en Cola!'}
              </h2>
              <p style={{ margin: '0 0 24px 0', color: '#6b7280' }}>
                {language === 'en'
                  ? 'All tickets will upload in background'
                  : 'Todos los boletos se subirán en segundo plano'}
              </p>
              <button
                onClick={() => setBulkScanCount(null)}
                style={styles.submitButton}
              >
                {language === 'en' ? 'Continue' : 'Continuar'}
              </button>
            </div>
          </div>
        )}

        {/* Camera Component (Android only) */}
        {showCamera && (
          <CameraComponent
            onCapture={handleCameraCapture}
            onClose={handleCameraClose}
          />
        )}

      </div>

      {/* Native Debug Panel - shows upload debug logs on iOS/Android */}
      {isNativeApp() && debugLogs.length > 0 && (
        <div style={{
          backgroundColor: '#1e293b',
          borderRadius: '8px',
          padding: '12px',
          marginTop: '16px',
          maxHeight: '200px',
          overflowY: 'auto'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '8px'
          }}>
            <span style={{ color: '#94a3b8', fontSize: '12px', fontWeight: '600' }}>
              Debug ({debugLogs.length})
            </span>
            <button
              onClick={() => setDebugLogs([])}
              style={{
                backgroundColor: '#475569',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '2px 8px',
                fontSize: '10px',
                cursor: 'pointer'
              }}
            >
              Clear
            </button>
          </div>
          {debugLogs.map((log, i) => (
            <div key={i} style={{
              color: log.includes('ERROR') ? '#f87171' : log.includes('SUCCESS') ? '#4ade80' : '#e2e8f0',
              fontSize: '10px',
              fontFamily: 'monospace',
              lineHeight: '1.4',
              borderBottom: '1px solid #334155',
              paddingBottom: '4px',
              marginBottom: '4px'
            }}>
              {log}
            </div>
          ))}
          {/* Also show global upload debug logs */}
          {(window as any).__uploadDebug?.slice(-10).map((log: string, i: number) => (
            <div key={`global-${i}`} style={{
              color: log.includes('FAILED') ? '#f87171' : log.includes('SUCCESS') ? '#4ade80' : '#fbbf24',
              fontSize: '10px',
              fontFamily: 'monospace',
              lineHeight: '1.4',
              borderBottom: '1px solid #334155',
              paddingBottom: '4px',
              marginBottom: '4px'
            }}>
              {log}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};